import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { trackEvent } from "@/lib/tracker";
import { LogoMark } from "./Logo";

const GREETING =
  "Good day. I can help you identify a product, structure a requirement, explore our supply categories or prepare an RFQ for the Royal Deals commercial team. What are you looking for?";

const SUMMARY_RE = /<requirement_summary>([\s\S]*?)<\/requirement_summary>/;
const stripSummary = (t) => t.replace(SUMMARY_RE, "").trim();
const parseSummary = (t) => {
  const m = t.match(SUMMARY_RE);
  if (!m) return null;
  try { return JSON.parse(m[1]); } catch { return null; }
};

export const RoyalAi = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoff, setHandoff] = useState({ name: "", email: "" });
  const [handoffSent, setHandoffSent] = useState(false);
  const sessionRef = useRef(crypto.randomUUID());
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, handoffOpen, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    if (messages.length === 1) trackEvent("ai_started");
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = JSON.parse(part.slice(6));
          if (data.delta) {
            full += data.delta;
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { role: "assistant", content: stripSummary(full) };
              return next;
            });
          }
          if (data.error) toast.error(data.error);
        }
      }
      const s = parseSummary(full);
      if (s) {
        setSummary(s);
        trackEvent("ai_summary_generated");
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", content: "I am temporarily unavailable. You can reach our commercial team directly via the Contact page." };
        return next;
      });
    }
    setBusy(false);
  };

  const doHandoff = async () => {
    if (!handoff.name.trim() || !handoff.email.trim()) {
      toast.error("Please provide your name and business email.");
      return;
    }
    try {
      await fetch(`${API}/ai/handoff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, name: handoff.name, email: handoff.email }),
      });
      setHandoffSent(true);
      setHandoffOpen(false);
      setMessages((m) => [...m, { role: "assistant", content: "Thank you — your conversation and requirement details have been passed to our commercial team, who will respond directly. Nothing needs to be repeated." }]);
    } catch {
      toast.error("Could not complete the handoff. Please try again.");
    }
  };

  const summaryQuery = summary
    ? `/request?${new URLSearchParams({
        source: "ai",
        category: summary.product_class || "",
        product: summary.solution || summary.specification || "",
        quantity: summary.quantity || "",
        destination: summary.destination || "",
        part_number: summary.part_number || "",
        company: summary.company || "",
        notes: `Royal AI conversation summary — Specification: ${summary.specification || "n/a"}; Delivery: ${summary.delivery || "n/a"}; Target price: ${summary.target_price || "n/a"}; Incoterm: ${summary.incoterm || "n/a"}. ${summary.notes || ""}`,
      }).toString()}`
    : "/request?source=ai";

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            onClick={() => setOpen(true)}
            data-testid="royal-ai-open"
            className="fixed bottom-6 right-6 z-[70] flex items-center gap-3 bg-white hairline shadow-medium pl-3 pr-5 py-3 hover:border-gold/50 transition-colors"
          >
            <span className="relative">
              <LogoMark size={24} />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-gold" />
            </span>
            <span className="text-sm font-medium text-midnight">Ask Royal AI</span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="royal-ai-panel"
            className="fixed z-[70] inset-x-0 bottom-0 top-14 sm:inset-x-auto sm:top-auto sm:bottom-6 sm:right-6 sm:w-[400px] sm:h-[600px] sm:max-h-[80vh] bg-white hairline shadow-medium flex flex-col"
          >
            <div className="bg-midnight px-5 py-4 flex items-center gap-3 shrink-0">
              <LogoMark size={26} />
              <div className="leading-tight">
                <p className="font-heading font-semibold tracking-[0.12em] text-white text-sm">ROYAL AI</p>
                <p className="text-[11px] text-white/50">Commercial &amp; Product Assistant</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="royal-ai-close" className="ml-auto text-white/60 hover:text-white transition-colors" aria-label="Close Royal AI">
                <X size={18} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-white" data-testid="royal-ai-messages">
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "text-right" : ""}>
                  <div
                    className={`inline-block max-w-[92%] text-left px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user" ? "bg-midnight text-white" : "bg-[#F3F4F6] text-midnight/80"
                    }`}
                  >
                    {m.content || (busy && i === messages.length - 1 ? <Loader2 size={14} className="animate-spin text-gold" /> : "")}
                  </div>
                </div>
              ))}

              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {[["Structure a requirement", "I would like to structure a hardware requirement."], ["Explore product categories", "What product categories does Royal Deals FZ-LLC supply?"]].map(([label, msg]) => (
                    <button
                      key={label}
                      onClick={() => send(msg)}
                      data-testid={`royal-ai-quick-${label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-xs font-medium text-midnight/70 bg-white hairline px-3.5 py-2 hover:border-gold hover:text-gold transition-colors"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {summary && (
                <div className="border border-gold/40 bg-gold/[0.05] p-5" data-testid="royal-ai-summary">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-4">Requirement Summary</p>
                  <dl className="space-y-2 text-sm">
                    {Object.entries(summary).filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="flex gap-3">
                        <dt className="text-[10px] font-bold uppercase tracking-wide text-midnight/40 w-24 shrink-0 pt-0.5">{k.replace(/_/g, " ")}</dt>
                        <dd className="text-midnight/80">{v}</dd>
                      </div>
                    ))}
                  </dl>
                  <Link
                    to={summaryQuery}
                    data-testid="royal-ai-submit-summary"
                    className="mt-5 block text-center bg-midnight text-white text-sm font-medium px-5 py-3 hover:bg-midnight-light transition-colors"
                  >
                    Submit to Royal Deals FZ-LLC
                  </Link>
                </div>
              )}

              {handoffOpen && !handoffSent && (
                <div className="border border-midnight/15 bg-[#FAFAFA] p-5" data-testid="royal-ai-handoff">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-midnight mb-1">Contact Commercial Team</p>
                  <p className="text-xs text-midnight/50 mb-4 leading-relaxed">This conversation will be attached to your enquiry.</p>
                  <input
                    placeholder="Your name"
                    value={handoff.name}
                    onChange={(e) => setHandoff((h) => ({ ...h, name: e.target.value }))}
                    data-testid="royal-ai-handoff-name"
                    className="w-full bg-white border border-midnight/15 px-3 py-2.5 text-sm text-midnight outline-none focus:border-gold mb-3"
                  />
                  <input
                    placeholder="Business email"
                    type="email"
                    value={handoff.email}
                    onChange={(e) => setHandoff((h) => ({ ...h, email: e.target.value }))}
                    data-testid="royal-ai-handoff-email"
                    className="w-full bg-white border border-midnight/15 px-3 py-2.5 text-sm text-midnight outline-none focus:border-gold mb-4"
                  />
                  <button
                    onClick={doHandoff}
                    data-testid="royal-ai-handoff-send"
                    className="w-full bg-gold text-midnight text-sm font-semibold px-5 py-3 hover:bg-gold-light transition-colors"
                  >
                    Send to Commercial Team
                  </button>
                </div>
              )}
            </div>

            <div className="border-t border-midnight/10 px-4 py-3 bg-white shrink-0">
              <div className="flex gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Type your message…"
                  data-testid="royal-ai-input"
                  className="flex-1 bg-[#F9FAFB] border border-midnight/10 px-4 py-2.5 text-sm text-midnight placeholder:text-midnight/35 outline-none focus:border-gold/60 transition-colors"
                />
                <button
                  onClick={() => send()}
                  disabled={busy || !input.trim()}
                  data-testid="royal-ai-send"
                  className="bg-midnight text-white px-3.5 hover:bg-midnight-light transition-colors disabled:opacity-40"
                  aria-label="Send message"
                >
                  <Send size={15} />
                </button>
              </div>
              <button
                onClick={() => setHandoffOpen(!handoffOpen)}
                data-testid="royal-ai-contact-team"
                className="mt-2.5 text-[11px] font-medium text-midnight/50 hover:text-gold transition-colors"
              >
                Prefer a human? Contact the commercial team →
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
